import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import yaml from 'js-yaml';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from '../config';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});

function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject('No user');
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session);
    });
  });
}

// Validate raw YAML text. Returns { ok, error } — error carries a human-readable
// message (with line number when js-yaml provides one).
function validateYaml(text) {
  if (!text.trim()) return { ok: false, error: 'Document is empty.' };
  let parsed;
  try {
    parsed = yaml.load(text);
  } catch (err) {
    const line = err && err.mark && typeof err.mark.line === 'number' ? ` (line ${err.mark.line + 1})` : '';
    const reason = (err && err.reason) || (err && err.message) || 'Invalid YAML';
    return { ok: false, error: `${reason}${line}` };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'Top-level YAML must be a mapping (key: value pairs).' };
  }
  return { ok: true, error: null };
}

export default function RawYamlEditor({ repo, readOnly = false, onSaved }) {
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const textareaRef = useRef(null);

  const dirty = content !== savedContent;
  const validation = useMemo(() => validateYaml(content), [content]);

  const fetchYaml = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSavedMsg(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed (${response.status})`);
      }
      const text = await response.text();
      setContent(text);
      setSavedContent(text);
    } catch (err) {
      setError('Failed to load rho.yaml: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  }, [repo]);

  useEffect(() => {
    if (repo) fetchYaml();
  }, [repo, fetchYaml]);

  const handleSave = async () => {
    if (!validation.ok) return;
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'text/yaml' },
        body: content,
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed (${response.status})`);
      }
      setSavedContent(content);
      setSavedMsg('rho.yaml saved. The Configuration tab has been re-synced.');
      // Re-sync the parent Configuration form so its fields reflect the new YAML
      // and a later form save can't overwrite this edit with stale values.
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      setError('Failed to save rho.yaml: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!dirty) return;
    if (!window.confirm('Discard unsaved changes?')) return;
    setContent(savedContent);
    setError(null);
    setSavedMsg(null);
  };

  // Tab key inserts two spaces rather than moving focus — expected in a code editor.
  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && !readOnly) {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = content.slice(0, start) + '  ' + content.slice(end);
      setContent(next);
      requestAnimationFrame(() => {
        if (textareaRef.current) textareaRef.current.setSelectionRange(start + 2, start + 2);
      });
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading rho.yaml…</div>;
  }

  return (
    <div style={styles.container} className="rawyaml-editor">
      <style>{rawYamlEditorStyles}</style>
      <div style={styles.heading}>
        <div>
          <h2 style={styles.title}>rho.yaml</h2>
          <p style={styles.subtitle}>
            Direct editor for <code>{repo}/rho.yaml</code>. Changes commit straight to GitHub —
            saving here re-syncs the Configuration tab. Admins only; edit with care.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchYaml}
          disabled={saving}
          style={styles.reloadBtn}
        >
          Reload from GitHub
        </button>
      </div>

      <div style={{
        ...styles.statusBar,
        background: validation.ok ? '#e8f6ee' : '#fdf2f2',
        color: validation.ok ? '#1e7e45' : '#c0392b',
        borderColor: validation.ok ? '#c3e6cb' : '#fecaca',
      }}>
        <span style={{ fontWeight: 700 }}>{validation.ok ? '● Valid YAML' : '● Invalid YAML'}</span>
        {!validation.ok && <span>— {validation.error}</span>}
      </div>

      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        readOnly={readOnly}
        style={styles.textarea}
      />

      {error && <div style={styles.error}>{error}</div>}
      {savedMsg && <div style={styles.success}>{savedMsg}</div>}

      {readOnly ? (
        <div style={{ ...styles.note, marginTop: '16px' }}>
          This repository is archived — rho.yaml is read-only.
        </div>
      ) : (
        <div style={styles.actions}>
          <button
            type="button"
            onClick={handleRevert}
            disabled={!dirty || saving}
            style={{ ...styles.secondaryBtn, ...((!dirty || saving) ? styles.disabledBtn : {}) }}
          >
            Discard changes
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving || !validation.ok}
            style={{ ...styles.primaryBtn, ...((!dirty || saving || !validation.ok) ? styles.disabledBtn : {}) }}
            title={!validation.ok ? 'Fix YAML errors before saving' : undefined}
          >
            {saving ? 'Saving…' : dirty ? 'Save rho.yaml' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  );
}

const rawYamlEditorStyles = `
  .rawyaml-editor code {
    color: #2c3e50;
    background: #f1f3f5;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.92em;
  }
  .rawyaml-editor textarea {
    background-color: #1e2530 !important;
    color: #e6edf3 !important;
    border: 1px solid #e1e5e9 !important;
    border-radius: 8px !important;
    padding: 16px !important;
    font-family: Consolas, Monaco, 'Courier New', monospace !important;
    font-size: 13px !important;
    line-height: 1.6 !important;
    tab-size: 2;
  }
`;

const styles = {
  container: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e1e5e9',
  },
  heading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '16px',
  },
  title: {
    color: '#2c3e50',
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
    paddingBottom: '6px',
    borderBottom: '2px solid #3498db',
    display: 'inline-block',
  },
  subtitle: {
    color: '#7f8c8d',
    fontSize: '13px',
    margin: '10px 0 0 0',
    maxWidth: '680px',
    lineHeight: 1.5,
  },
  reloadBtn: {
    flexShrink: 0,
    background: 'white',
    border: '1px solid #e1e5e9',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#2c3e50',
    cursor: 'pointer',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '13px',
    marginBottom: '10px',
  },
  textarea: {
    width: '100%',
    minHeight: '560px',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  primaryBtn: {
    background: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },
  secondaryBtn: {
    background: '#ecf0f1',
    color: '#2c3e50',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontWeight: 500,
    fontSize: '14px',
    cursor: 'pointer',
  },
  disabledBtn: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  loading: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center',
    color: '#7f8c8d',
    border: '1px solid #e1e5e9',
  },
  error: {
    color: '#e74c3c',
    background: '#fdf2f2',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    border: '1px solid #fecaca',
    fontSize: '13px',
    whiteSpace: 'pre-wrap',
  },
  success: {
    background: '#d4edda',
    border: '1px solid #c3e6cb',
    color: '#155724',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    fontSize: '13px',
  },
  note: {
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    color: '#856404',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '13px',
  },
};
